import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, errorText } from '../../api';
import { t, tl, tn, tx } from '../../i18n';
import { dayLabel, initials } from '../../lib/format';
import { useLive } from '../../lib/live';
import { useToast } from '../../lib/toast';
import { useClientApp } from '../context';
import { usePage } from '../page';
import { ChatComposer, isChatFile } from './common/Composer';
import { FileLoadError, loadChatFile, useOpenFile } from './common/files';
import { ChatMessages } from './common/Messages';
import { threadId } from './common/store';
import type { AppThread, ChatMessage, SendResult } from './common/types';
import { useMarkRead } from './common/useMarkRead';
import { appChat, appOpenThread, chatPath } from './data';
import { FileSheet } from './FileSheet';
import { LoadError } from './LoadError';

const TOO_LARGE = tl('Die Datei ist zu groß für den Chat. Bitte unter „Finanzbuchhaltung“ hochladen.');

// Chat mit einem Ansprechpartner der Kanzlei (für eine Firma).
export function ChatView() {
  const { clientId = '', staffId = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { clients } = useClientApp();
  const base = `/api/app/chat/${clientId}/${staffId}`;
  const { thread, gone, failed, reload, setThread } = useThread(base, clientId);
  const overview = appChat.useData();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useOpenFile();

  // Zurück (auch die Zurück-Geste) führt zur Liste, auch wenn der Chat direkt geöffnet wurde (z. B. über „Lesen“).
  const fixed = useRef(false);
  useEffect(() => {
    if (fixed.current || (location.state as { fromList?: boolean } | null)?.fromList) return;
    fixed.current = true;
    navigate('/app/nachrichten', { replace: true });
    navigate(location.pathname, { state: { fromList: true } });
  }, [location, navigate]);

  appOpenThread.useRegister(threadId(clientId, staffId));
  useMarkRead(threadId(clientId, staffId), thread?.messages, (until) => api.post(`${base}/read`, { until }));

  const name = thread?.contact.name ?? t('Nachrichten');
  usePage({
    title: name,
    crumbs: [{ label: t('Start'), to: '/app' }, { label: t('Nachrichten'), to: '/app/nachrichten' }, { label: name }],
    back: '/app/nachrichten',
    nav: 'nachrichten',
  });

  // Der Ansprechpartner ist nicht mehr in der Liste: zurück zur Liste.
  if (gone) return <Navigate to="/app/nachrichten" replace />;
  if (!thread) return failed ? <LoadError onRetry={reload} /> : <div className="spinner" aria-label={t('Lädt')} />;
  const { contact } = thread;

  const sent = (r: SendResult) => {
    // Aus einem alten Fenster an einen früheren Ansprechpartner: Der Server hat umgeleitet, dorthin wechseln.
    if (r.thread && r.thread !== staffId) {
      navigate(chatPath(clientId, r.thread), { replace: true, state: { fromList: true } });
      return;
    }
    setThread((th) =>
      th && !th.messages.some((m) => m.id === r.message.id) ? { ...th, messages: [...th.messages, r.message] } : th,
    );
    appChat.reload();
  };

  const sendText = async (text: string) => sent(await api.post<SendResult>(`${base}/messages`, { text }));

  const sendFile = async (f: File) => {
    if (!isChatFile(f)) {
      toast(t('Bitte eine PDF oder ein Foto anhängen'));
      return;
    }
    toast(t('Datei wird gesendet …'));
    const form = new FormData();
    form.append('file', f, f.name);
    try {
      sent(await api.post<SendResult>(`${base}/files`, form));
      toast(t('Datei gesendet'));
    } catch (err) {
      // 413 auch dann, wenn schon der Proxy vor der App die Datei abgelehnt hat (ohne Text der App)
      toast(err instanceof ApiError && err.status === 413 ? t(TOO_LARGE) : errorText(err));
    }
  };

  const openFile = async (m: ChatMessage) => {
    if (!m.file) return;
    try {
      const loaded = await loadChatFile(`/api/app/chat/${clientId}/files/${m.file.id}`, m.file);
      const day = dayLabel(m.at);
      const meta =
        m.sender === 'client'
          ? t('von Ihnen im Chat · {day}', { day })
          : t('von {name} im Chat · {day}', { name: contact.name, day });
      setFile({ ...loaded, meta });
    } catch (err) {
      toast(err instanceof FileLoadError && err.status === 404 ? t('Datei nicht gefunden') : t('Download nicht möglich'));
    }
  };

  // Mehrere Firmen: Firma nennen, damit gleichnamige Ansprechpartner unterscheidbar sind. Die Chat-Übersicht
  // kennt eine gerade freigegebene Firma schon, bevor die Sitzung neu geladen ist.
  const several = clients.length > 1 || (overview?.firms.length ?? 0) > 1;
  return (
    <>
      <div className="hello">
        <h2>{contact.name}</h2>
        <p>{t('{role} · Ihre Kanzlei.', { role: tx(contact.role) })}</p>
      </div>
      <div className="chat chat-page">
        <div className="chat-head">
          <span className="avc" aria-hidden="true">
            {initials(contact.name)}
          </span>
          <div>
            <b>{contact.name}</b>
            <small>
              {tx(contact.role)}
              {several ? ` · ${thread.clientName}` : ''}
            </small>
          </div>
        </div>
        <ChatMessages
          messages={thread.messages}
          isMine={(m) => m.sender === 'client'}
          onOpenFile={(m) => void openFile(m)}
          empty={t('Noch keine Nachrichten. Schreiben Sie {name} einfach hier, die Antwort kommt in diesem Chat.', {
            name: contact.name,
          })}
        />
        {contact.old ? (
          <div className="chat-in chat-ro">
            {tn('{name} ist nicht mehr für Sie zuständig. Bitte schreiben Sie an {contacts}.', {
              name: contact.name,
              contacts: thread.writable.map((w, i) => (
                <span key={w.id}>
                  {i > 0 && ` ${t('oder')} `}
                  <button
                    type="button"
                    className="linkbtn"
                    onClick={() => navigate(chatPath(clientId, w.id), { replace: true, state: { fromList: true } })}
                  >
                    {w.name}
                  </button>
                </span>
              )),
            })}
          </div>
        ) : (
          <ChatComposer
            key={threadId(clientId, staffId)}
            clipTitle={t('PDF oder Foto anhängen')}
            inputRef={inputRef}
            onSendText={sendText}
            onSendFile={(f) => void sendFile(f)}
          />
        )}
      </div>
      {file && <FileSheet file={file} onClose={() => setFile(null)} />}
    </>
  );
}

// Lädt den Chat und lädt bei Live-Ereignissen neu. gone = Chat gibt es für diesen Login nicht (mehr).
// failed = Laden hat aus einem anderen Grund nicht geklappt (z. B. Serverfehler).
function useThread(url: string, clientId: string) {
  const [thread, setThread] = useState<AppThread>();
  const [gone, setGone] = useState(false);
  const [failed, setFailed] = useState(false);
  const current = useRef(url);
  current.current = url;

  const load = useCallback(async () => {
    const u = current.current;
    try {
      const t = await api.get<AppThread>(u);
      if (current.current === u) {
        setThread(t);
        setFailed(false);
      }
    } catch (err) {
      if (current.current !== u) return;
      if (err instanceof ApiError && (err.status === 404 || err.status === 403)) setGone(true);
      else setFailed(true);
    }
  }, []);

  useEffect(() => {
    setThread(undefined);
    setGone(false);
    setFailed(false);
    void load();
  }, [url, load]);

  useLive(['chat'], (e) => {
    if (!e.clientId || e.clientId === clientId) void load();
  });
  const reload = useCallback(() => {
    setFailed(false);
    void load();
  }, [load]);
  return { thread, gone, failed, reload, setThread };
}
